import { retrieveLaunchParams } from '@telegram-apps/sdk-react'

const SESSION_STORAGE_KEY = 'kem_startparam'

/**
 * Prefix-to-route mapping.
 * Each key is a prefix (including trailing underscore).
 * The value after the prefix becomes the route parameter.
 */
const ROUTE_MAP: Record<string, (value: string) => string> = {
  'claim_': (code) => `/payment-link/${code}`,
  'payment_': (txId) => `/transaction-details/${txId}`,
}

/**
 * Extracts tgWebAppStartParam from a raw URL/hash string.
 */
function extractFromRaw(raw: string): string | null {
  const cleaned = raw.replace(/^[^?#]*[?#]/, '').replace(/[?#]/g, '&')
  const params = new URLSearchParams(cleaned)
  return params.get('tgWebAppStartParam') || null
}

/**
 * Extracts the startapp parameter from all available sources.
 *
 * Priority:
 * 1. Telegram SDK parsed launch params
 * 2. URL query parameter ?startapp=XXX (browser support)
 * 3. URL hash tgWebAppStartParam (Telegram web clients)
 * 4. SessionStorage tapps/launchParams (SDK persistence)
 * 5. SessionStorage kem_startparam (our own persistence for auth flow survival)
 */
export function extractStartParam(): string | null {
  // 1. Telegram SDK
  try {
    const lp = retrieveLaunchParams() as Record<string, unknown>
    const sdkValue = (lp.tgWebAppStartParam as string) ?? null
    if (sdkValue) return sdkValue
  } catch {
    // SDK not initialized — not in Telegram
  }

  // 2. URL query parameter ?startapp=XXX (browser deep link)
  try {
    const urlParams = new URLSearchParams(window.location.search)
    const startapp = urlParams.get('startapp')
    if (startapp) return startapp
  } catch {
    // URL parsing failed
  }

  // 3. URL hash (Telegram web clients)
  try {
    const hashValue = extractFromRaw(window.location.hash)
    if (hashValue) return hashValue
  } catch {
    // Hash parsing failed
  }

  // 4. SessionStorage — Telegram SDK persistence
  try {
    const raw = window.sessionStorage.getItem('tapps/launchParams')
    if (raw) {
      const parsed = JSON.parse(raw)
      if (typeof parsed === 'string') {
        const value = extractFromRaw(parsed)
        if (value) return value
      }
    }
  } catch {
    // Not available
  }

  // 5. SessionStorage — our own persistence (survives auth flow page refreshes)
  try {
    const saved = window.sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (saved) return saved
  } catch {
    // Not available
  }

  return null
}

/**
 * Resolves a startparam value to a route path.
 * Returns null for unrecognized prefixes (e.g., 'debug').
 */
export function resolveStartParamRoute(param: string): string | null {
  for (const [prefix, toRoute] of Object.entries(ROUTE_MAP)) {
    if (param.startsWith(prefix)) {
      const value = param.slice(prefix.length)
      if (value) return toRoute(value)
    }
  }
  return null
}

/**
 * Persists the raw startparam to sessionStorage so it survives
 * page refreshes during the auth flow.
 */
export function persistStartParam(param: string): void {
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, param)
  } catch {
    // Storage not available
  }
}

/**
 * Clears the persisted startparam from sessionStorage.
 */
export function clearPersistedStartParam(): void {
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY)
  } catch {
    // Storage not available
  }
}
